import { formatMoney } from './format';

// Skip, fallback and cancel reasons graphy writes (send_decision.rb,
// suppression.rb). An unknown code still shows, as is.
export const REASON_LABELS: Record<string, string> = {
  missed: 'Send time passed',
  kill_switch: 'Kill switch off',
  mode_off: 'Engine off',
  shadow: 'Shadow mode (not sent)',
  user_inactive: 'Account inactive',
  deactivated: 'Worker deactivated',
  permanently_removed: 'Permanently removed',
  suspended: 'Suspended',
  background_check_declined: 'Background check declined',
  interview_declined: 'Interview declined',
  data_deletion_requested: 'Asked to delete data',
  do_not_contact: 'Do not contact',
  paused: 'Journeys paused by ops',
  holdout: 'Holdout group',
  // The same words as the journey pill (messagingLabels exitLabel, Jira 61299; review D-08).
  journey_disabled: 'Journey switched off',
  not_allowlisted: 'Not on the allowlist',
  outside_pilot: 'Outside the pilot area',
  before_cohort: 'Signed up before the cohort date',
  already_rated: 'Already rated the shift',
  shift_cancelled: 'Shift cancelled',
  shift_started: 'Shift already started',
  shift_time_changed: 'Shift time changed',
  // A held J6 shift alert whose job was cancelled, filled or started (review B-03).
  job_unavailable: 'Shift no longer open',
  step_fixed: 'Fixed before it went out',
  // A held "background check started" whose check had moved on (review A-02).
  status_moved_on: 'Status changed before it went out',
  // A re-announced clearance (bug hunt 15): the first one is over 7 days old.
  stale_clearance: 'Cleared long ago, not announced again',
  // Sent back to a step before the held "ready to work" SMS went out (bug hunt 16).
  not_cleared: 'No longer cleared when it was due',
  no_shifts_repeat: 'No shifts nearby (already told)',
  no_channel: 'No channel allowed',
  no_phone: 'No phone number',
  // graphy sends no SMS without a zip code: its local time is unknown (Jira 61259 item 2).
  no_zip: 'No zip code, so no SMS',
  unknown_zip: 'Zip code not in our zip list, so no SMS',
  push_unreachable: 'No device for push',
  no_email: 'No email address, or email turned off',
  quiet_hours: 'Quiet hours',
  missing_template: 'No copy for this step',
  missing_variable: 'Missing data for the text',
  link_failed: 'Link could not be created',
  tax_guide_missing: 'Tax guide link not set — set it under Journeys',
  sms_disabled: 'SMS off for this journey',
  sms_paused: 'SMS paused (STOP rate)',
  goal_met: 'Goal already met',
  journey_ended: 'Journey ended',
  stale_sending: 'Send did not finish (retried)',
  // The SMS fallback reasons name the reason only: they also label skips,
  // where nothing was sent (QA 61304 F-02). The timeline row adds the channel
  // that carried the message, when one did.
  no_sms_consent: 'No SMS consent',
  sms_opted_out: 'Opted out of SMS',
  sms_weekly_cap: 'SMS weekly limit',
  sms_budget: 'SMS budget used up',
  sms_undeliverable: 'Phone cannot get SMS',
  sms_opt_in_pending: 'SMS waits for the sign-up confirmation',
};

export const JOURNEY_NAMES: Record<string, string> = {
  J1: 'Finish your profile',
  J2: 'Tax form',
  J3: 'Direct deposit',
  J4: 'Background check',
  J5: 'I-9 verification',
  J6: 'First shift',
  J7: 'Get to 3 shifts',
  J8: 'Activate at 5 shifts',
  J9: 'Win-back',
  TX: 'Transactional',
};

export const CHANNEL_LABELS: Record<string, string> = {
  sms: 'SMS',
  push: 'Push',
  email: 'Email',
  ops: 'Call',
};

export const EVENT_LABELS: Record<string, string> = {
  journey_settings_changed: 'Settings changed',
  sms_paused: 'SMS paused automatically',
  sms_resumed: 'SMS resumed',
  copy_published: 'Copy published',
  copy_reverted: 'Copy reverted',
  copy_reset: 'Copy reset to default',
  sms_consent_recorded: 'SMS consent recorded',
  sms_consent_declined: 'SMS consent declined',
  sms_consent_withdrawn: 'SMS consent withdrawn',
};

export const OUTCOME_LABELS: Record<string, string> = {
  completed_on_call: 'Completed on the call',
  will_finish: 'Will finish',
  not_interested: 'Not interested',
  no_answer: 'No answer',
  booked: 'Booked a shift',
  document_issue: 'Document issue',
};

export const CONSENT_SOURCE_LABELS: Record<string, string> = {
  signup_form: 'Sign-up form',
  phone_verification_backfill: 'Phone verification',
  start_keyword: 'START reply',
  settings: 'Settings',
};

export const OPT_OUT_SOURCE_LABELS: Record<string, string> = {
  keyword_stop: 'STOP reply',
  // GRAVY-10898's Twilio webhook (WorkerSmsOptout) writes this; production
  // rows exist from before Lifecycle (review N-05).
  stop_reply: 'STOP reply',
  shift_blast: 'Shift Blast reply',
  carrier_21610: 'Carrier block',
  admin: 'Admin',
  settings: 'Settings',
};

const lookup = (map: Record<string, string>, code: string) =>
  Object.prototype.hasOwnProperty.call(map, code) ? map[code] : code;

export const reasonLabel = (
  code: string,
): { label: string; known: boolean } => {
  const known = Object.prototype.hasOwnProperty.call(REASON_LABELS, code);
  return { label: known ? REASON_LABELS[code] : code, known };
};

// Why a manual enrol was refused (manual_enrol_service.rb sends
// "Cannot enrol: <rule>"; suppressed_<reason> uses REASON_LABELS).
const ENROL_FAILURE_LABELS: Record<string, string> = {
  unknown_journey: 'that journey does not exist',
  kill_switch: 'the kill switch is off',
  mode_off: 'the engine is off',
  already_active: 'the worker is already in a journey',
  journey_does_not_fit: "this journey does not fit the worker's stage now",
  journey_disabled: 'this journey is switched off',
  outside_pilot: 'the worker is outside the pilot zips',
  reentry_blocked: 'the worker cannot start this journey again yet',
};

// Settings fields as update_journey_settings_service.rb names them.
const FIELD_LABELS: Record<string, string> = {
  enabled: 'Journey on/off',
  sms_enabled: 'SMS on/off',
  holdout_percent: 'Holdout',
  high_demand_threshold: 'High-demand threshold',
  clear_sms_pause: 'Clear SMS pause',
  mode: 'Mode',
  cohort_start_on: 'Cohort start date',
  cohort_zip_prefixes: 'Zip prefixes',
  recipient_allowlist: 'Allowlist',
  monthly_sms_budget_cents: 'SMS budget',
  sms_segment_cost_cents: 'SMS segment cost',
  tax_guide_url: 'Tax guide link',
};

const SPECIAL_ERRORS: [RegExp, (...groups: string[]) => string][] = [
  [
    /^holdout_percent must be 0 to (\d+)$/,
    (max) => `Holdout must be 0 to ${max}%`,
  ],
  [
    /^monthly_sms_budget_cents must be a whole number from 0 to (\d+)$/,
    (max) =>
      `SMS budget must be from ${formatMoney(0)} to ${formatMoney(Number(max))}`,
  ],
  [
    /^sms_segment_cost_cents must be a number from 0 to ([\d.]+)$/,
    (max) => `SMS segment cost must be from 0¢ to ${max}¢`,
  ],
  [
    /^tax_guide_url must be text of at most (\d+) characters$/,
    (max) => `Tax guide link must be at most ${max} characters`,
  ],
  [
    /^tax_guide_url must be https$/,
    () => 'Tax guide link must start with https://',
  ],
  [
    /^cohort_start_on must be an ISO date$/,
    () => 'Cohort start date must be a valid date',
  ],
];

const friendlyPart = (part: string): string => {
  const enrol = /^Cannot enrol: (\w+)$/.exec(part);
  if (enrol) {
    const code = enrol[1];
    const suppressed = /^suppressed_(\w+)$/.exec(code);
    if (suppressed) {
      return `Cannot enrol: the worker gets no messages (${reasonLabel(suppressed[1]).label}).`;
    }
    return Object.prototype.hasOwnProperty.call(ENROL_FAILURE_LABELS, code)
      ? `Cannot enrol: ${ENROL_FAILURE_LABELS[code]}.`
      : part;
  }
  for (const [pattern, text] of SPECIAL_ERRORS) {
    const match = pattern.exec(part);
    if (match) return text(...match.slice(1));
  }
  const field = /^(\w+) (.*)$/.exec(part);
  if (field && Object.prototype.hasOwnProperty.call(FIELD_LABELS, field[1])) {
    return `${FIELD_LABELS[field[1]]} ${field[2]}`;
  }
  return part;
};

// Turns API field names and rule codes in an error into words an admin reads.
// Anything it does not know passes through unchanged.
export const friendlyLifecycleError = (message: string): string =>
  message.split('; ').map(friendlyPart).join('; ');

export const channelLabel = (code: string) => lookup(CHANNEL_LABELS, code);
export const eventLabel = (key: string) => lookup(EVENT_LABELS, key);
export const outcomeLabel = (code: string) => lookup(OUTCOME_LABELS, code);
export const consentSourceLabel = (code: string) =>
  lookup(CONSENT_SOURCE_LABELS, code);
export const optOutSourceLabel = (code: string) =>
  lookup(OPT_OUT_SOURCE_LABELS, code);

const text = (value: unknown): string | null =>
  typeof value === 'string' && value ? value : null;

// One plain sentence for worker events whose payload matters to an admin
// (graphy consent/phone_change_service.rb and record_service.rb). Null when
// the event label alone says it.
export const workerEventLine = (
  key: string,
  payload: Record<string, unknown> | null | undefined,
): string | null => {
  const data = payload ?? {};
  if (key === 'sms_consent_withdrawn') {
    const reason = text(data.reason);
    const why =
      reason === 'phone_changed'
        ? 'phone number changed'
        : reason?.replace(/_/g, ' ');
    const before = text(data.old_phone_last4);
    const after = text(data.new_phone_last4);
    const phones = before && after ? ` (…${before} → …${after})` : '';
    return why
      ? `SMS consent withdrawn: ${why}${phones}`
      : 'SMS consent withdrawn';
  }
  if (key === 'sms_consent_recorded' || key === 'sms_consent_declined') {
    const source = text(data.source);
    const prefix =
      key === 'sms_consent_recorded' ? 'SMS consent' : 'SMS consent declined';
    if (!source) return eventLabel(key);
    return source === 'start_keyword'
      ? `${prefix}: texted START`
      : `${prefix}: ${consentSourceLabel(source)}`;
  }
  return null;
};
